import axios from 'axios';
import { toast } from 'react-toastify';
import { sentryLog } from '../Utils/SentryLog';
import { getTrackdeskParams } from './GetTrackDeskParams';
/* global gtag */

const isShowConsole = process.env.NODE_ENV === 'development';

const API_BASE_URL = process.env.NODE_ENV === 'development' 
    ? process.env.REACT_APP_LOCAL_API_URL 
    : process.env.REACT_APP_API_URL;

const initiateLogin = async () => {
  try{
      const query = getTrackdeskParams();
      await fetch(`${API_BASE_URL}/api/set-redirect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // ✅ critical for session cookie
        body: JSON.stringify({ path: `/pricing${query}` })
      });
  
      window.location.href = `${API_BASE_URL}/auth/google`;
  }catch(err){
      isShowConsole && console.error('error in initiate login method: ', err);
      sentryLog(`Razorpay.js - initiateLogin failed: ${err?.message || err}`);
  }
}

const showSuccessToast = planType => {
  try{
    toast.success(
      <div className='verificationSuccessToast'>
        <span>
          Account upgraded! Welcome to {planType}! 🎉 
        </span>
      </div>,
      {
        className: "verificationSuccessToastContainer",
        autoClose: 4000, 
        closeButton: false
      }
    );
  }catch(err){
    isShowConsole && console.error("error in showSuccessToast: ", err);
    sentryLog(`Razorpay.js - showSuccessToast failed: ${err?.message || err}`);
  }
}
const showFailureToast = (openContactForm) => {
  try{
    toast.error(
      <div className='verificationFailureToast'>
        <span>
          Payment received, but verification failed. Please <a href="#" onClick={(e) => { e.preventDefault(); openContactForm();}}>Contact Support</a> for a manual upgrade or refund. 
        </span>
      </div>,
      {
        className: "verificationFailureToastContainer",
        autoClose: 8000, 
        closeButton: false
      }
    );
  }catch(err){
    isShowConsole && console.error("error in showFailureToast: ", err);
    sentryLog(`Razorpay.js - showFailureToast failed: ${err?.message || err}`);
  }
}
const showErrorToast = e => {
  try{
    toast.error(
      <div className='paymentFailureToast'>
        <span>
          Something went wrong. Please try again. 
        </span>
      </div>,
      {
        className: "paymentFailureToastContainer",
        autoClose: 4000, 
        closeButton: false
      }
    );
  }catch(err){
    isShowConsole && console.error("error in showErrorToast: ", err);
    sentryLog(`Razorpay.js - showErrorToast failed: ${err?.message || err}`);
  }
}

export const handleRazorpayPayment = async (planAmount, planType, setUser, openContactForm) => {
    //event tracker
        gtag('event', 'upgrade_clicked', {
          event_category: 'Button',
          event_label: 'Upgrade'
      });
    //
    let conversionRate = parseFloat(localStorage.getItem("conversionRate"));
    let planAmountVal = parseFloat(planAmount);
    let planAmountUSD = (planAmountVal / conversionRate).toFixed(2);
    try {
      const res = await fetch(`${API_BASE_URL}/api/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: planAmount }),
        credentials: "include",
      });

      if (res.status === 401) {
        localStorage.setItem("pendingPlanAmount", planAmount);
        localStorage.setItem("pendingPlanType", planType);
        initiateLogin();
        return;
      }

      isShowConsole && console.log('create order res data: ', res);
  
      const data = await res.json();
  
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: data.amount,
        currency: data.currency,
        name: "Easy Image to Text",
        description: "Upgrade to Premium Plan",
        order_id: data.orderId,
        handler: async function (response) {
          isShowConsole && console.log("Payment success. Razorpay response", response);
          
          //hit backend for payment verification
          try {
            const verificationRes = await fetch(`${API_BASE_URL}/api/verify-payment`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                planType: planType,
              }),
              credentials: "include",
            });
  
            const verificationData = await verificationRes.json();
  
            if (verificationData.success) {
              showSuccessToast(planType);
              //trackdesk conversion tracking
              try {
                window.trackdesk("easyimagetotext", "conversion", {
                  conversionType: "sale",
                  currencyCode: "USD",
                  amount: { value: planAmountUSD } 
                });
              } catch (e) {
                console.log('catch case entered');
                
                console.error("❌ Trackdesk razorpay conversion trigger failed:", e);
              }
              //
              try {
                const res = await axios.get(`${API_BASE_URL}/api/me`, { withCredentials: true });
                setUser(res.data.user || null);
              } catch (userErr) {
                setUser(null);
                isShowConsole && console.error("User fetch error:", userErr);
                sentryLog(`Razorpay.js - fetching user details post-verification failed: ${userErr?.message || userErr}`);
              }
            } else {//verification failed → No premium access granted
              showFailureToast(openContactForm);
              isShowConsole && console.error("Payment successful, but verification failed: " + verificationData.error);
              sentryLog(`Razorpay.js - Payment successful, but verification failed: ${verificationData.error} | response: ${JSON.stringify(response)}`);
            }
          } catch (err) {//verification error → No premium access granted
            showFailureToast(openContactForm);
            isShowConsole && console.error("Payment successful, but something went wrong during verification:", err);
            sentryLog(`Razorpay.js - Payment successful, but something went wrong during verification: ${err?.message || err} | response: ${JSON.stringify(response)}`);
          }
          //
        },
        theme: { color: "#3399cc" },
      };
  
      const razorpay = new window.Razorpay(options);
      razorpay.open();
  
    } catch (err) {
      isShowConsole && console.error("Payment failed", err);
      sentryLog(`Razorpay.js - handleRazorpayPayment failed: ${err?.message || err}`);
      showErrorToast();
    }
  };
  